import { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

export const AuthContext = createContext(null);

const TOKEN_KEY = 'cms_auth_token';
const USER_KEY = 'cms_auth_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Initial auth check loading
  const [isSubmitting, setIsSubmitting] = useState(false); // Form submission loading
  const [error, setError] = useState(null);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [temporaryToken, setTemporaryToken] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (storedToken && storedUser) {
        try {
          // Verify token is still valid by calling /me endpoint
          const response = await authService.getCurrentUser();
          setToken(storedToken);
          setUser(response.data);
        } catch (err) {
          // Token is invalid, clear storage
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setIsSubmitting(true);
    setError(null);
    setRequires2FA(false);
    setTemporaryToken(null);

    try {
      const response = await authService.login(email, password);
      const data = response.data;

      // Check if 2FA is required
      if (data.requires2FA) {
        setRequires2FA(true);
        setTemporaryToken(data.temporaryToken);
        setIsSubmitting(false);
        return { success: false, requires2FA: true, message: data.message };
      }

      // No 2FA - complete login
      const { token: newToken, user: userData } = data;

      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      setToken(newToken);
      setUser(userData);
      setIsSubmitting(false);

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      setIsSubmitting(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  const verify2FA = useCallback(async (code) => {
    if (!temporaryToken) {
      return { success: false, error: 'No 2FA session found' };
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authService.verify2FA(temporaryToken, code);
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      setToken(newToken);
      setUser(userData);
      setRequires2FA(false);
      setTemporaryToken(null);
      setIsSubmitting(false);

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Invalid verification code';
      setError(errorMessage);
      setIsSubmitting(false);
      return { success: false, error: errorMessage };
    }
  }, [temporaryToken]);

  const cancel2FA = useCallback(() => {
    setRequires2FA(false);
    setTemporaryToken(null);
    setError(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await authService.logout();
      }
    } catch (err) {
      // Ignore logout errors, clear local state anyway
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);
      setError(null);
      setRequires2FA(false);
      setTemporaryToken(null);
    }
  }, [token]);

  const refreshToken = useCallback(async () => {
    if (!token) return false;

    try {
      const response = await authService.refreshToken();
      const { token: newToken } = response.data;

      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);

      return true;
    } catch (err) {
      // Refresh failed, logout user
      await logout();
      return false;
    }
  }, [token, logout]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    token,
    isLoading,
    isSubmitting,
    isAuthenticated: !!token && !!user,
    error,
    // 2FA state
    requires2FA,
    temporaryToken,
    // Methods
    login,
    verify2FA,
    cancel2FA,
    logout,
    refreshToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
